import React, { useContext, useState, useEffect } from "react";
import {
    WizardView,
    useWizardPage,
    useSyncedField,
    useRuntimeField,
    FinishPage,
} from "../../components/Wizard";
import { gql, useQuery } from "@apollo/client";
import StarekContext from "../../context/StarekContext";
import Spinner from "../../components/Spinner";
import {
    DeviceTypePage,
    SignalsPage,
} from "../deviceProfile/AddDeviceProfileWizard";

const GET_DEVICE_PROFILES = gql`
    query GetDeviceProfiles($plant_id: Int!) {
        deviceProfiles(where: { plant_id: { equals: $plant_id } }) {
            id
            name
            description
            configuration
        }
    }
`;

function ConfigSourcePage() {
    const starekContext = useContext(StarekContext);
    const [selectedProfileName, setSelectedProfileName] = useRuntimeField(
        "selected_profile_name",
        null
    );
    const [configSource, setConfigSource] = useSyncedField(
        "config_source",
        "existing"
    );
    const [selectedProfileId, setSelectedProfileId] = useSyncedField(
        "selected_profile_id",
        ""
    );
    const [newProfileName, setNewProfileName] = useSyncedField(
        "new_profile_name",
        ""
    );
    const [newProfileDescription, setNewProfileDescription] = useSyncedField(
        "new_profile_description",
        ""
    );

    const [selectedConfigData, setSelectedConfigData] = useSyncedField(
        "selected_config_data",
        null
    );

    const { data, loading, error } = useQuery(GET_DEVICE_PROFILES, {
        variables: { plant_id: starekContext?.lastUsedPlant?.id },
        skip: !starekContext?.lastUsedPlant?.id,
        client: starekContext.engineClient,
    });

    const deviceProfiles = data?.deviceProfiles || [];

    useEffect(() => {
        if (configSource === "existing" && selectedProfileId) {
            const profile = deviceProfiles.find(
                (p) => String(p.id) === String(selectedProfileId)
            );
            if (profile) {
                if (JSON.stringify(selectedConfigData) !== JSON.stringify(profile.configuration)) {
                     setSelectedConfigData(profile.configuration);
                }
                if (selectedProfileName !== profile.name) {
                     setSelectedProfileName(profile.name);
                }
            }
        } else if (configSource === "new") {
            if (selectedConfigData !== null) setSelectedConfigData(null);
            if (selectedProfileName !== null) setSelectedProfileName(null);
        }
    }, [configSource, selectedProfileId, deviceProfiles, setSelectedProfileName, setSelectedConfigData, selectedConfigData, selectedProfileName]);

    if (loading) return <Spinner />;
    if (error) return <div className="text-danger">Error loading profiles</div>;

    return (
        <div>
            <h3 className="mb-3">Configuration Source</h3>
            <p className="text-muted mb-4">
                Choose how you want to configure this device.
            </p>

            <div className="mb-4">
                <div className="form-check mb-2">
                    <input
                        className="form-check-input"
                        type="radio"
                        name="configSource"
                        id="sourceExisting"
                        value="existing"
                        checked={configSource === "existing"}
                        onChange={(e) => setConfigSource(e.target.value)}
                    />
                    <label className="form-check-label" htmlFor="sourceExisting">
                        Use existing Device Profile
                    </label>
                </div>
                <div className="form-check">
                    <input
                        className="form-check-input"
                        type="radio"
                        name="configSource"
                        id="sourceNew"
                        value="new"
                        checked={configSource === "new"}
                        onChange={(e) => setConfigSource(e.target.value)}
                    />
                    <label className="form-check-label" htmlFor="sourceNew">
                        Create New Configuration
                    </label>
                </div>
            </div>

            {configSource === "existing" && (
                <div className="mb-3">
                    <label className="form-label">Select Device Profile</label>
                    <select
                        className="form-select"
                        value={selectedProfileId}
                        onChange={(e) => setSelectedProfileId(e.target.value)}
                    >
                        <option value="">-- Select a Profile --</option>
                        {deviceProfiles.map((p) => (
                            <option key={p.id} value={p.id}>
                                {p.name}
                            </option>
                        ))}
                    </select>
                </div>
            )}

            {configSource === "new" && (
                <div>
                    <div className="mb-3">
                        <label className="form-label">
                            Name <span className="text-danger">*</span>
                        </label>
                        <input
                            type="text"
                            className="form-control"
                            value={newProfileName}
                            onChange={(e) => setNewProfileName(e.target.value)}
                            placeholder="e.g. Temperature Sensor v1"
                        />
                    </div>
                    <div className="mb-3">
                        <label className="form-label">Description</label>
                        <textarea
                            className="form-control"
                            rows="3"
                            value={newProfileDescription}
                            onChange={(e) =>
                                setNewProfileDescription(e.target.value)
                            }
                            placeholder="Optional description..."
                        ></textarea>
                    </div>
                </div>
            )}
        </div>
    );
}

function ReviewPage() {
    const { runtimeVariables } = useWizardPage();
    const [saveAsProfile, setSaveAsProfile] = useSyncedField(
        "save_as_profile",
        false
    );
    
    const isNew = runtimeVariables?.config_source === "new";
    const selectedProfile = runtimeVariables?.selected_profile_name;

    return (
        <div>
            <h3 className="mb-3">Review & Finish</h3>
            
            <div className="card mb-3">
                <div className="card-body">
                    <h5 className="card-title">Configuration Summary</h5>
                    <dl className="row mb-0">
                        <dt className="col-sm-4">Source</dt>
                        <dd className="col-sm-8">
                            {isNew ? "New Configuration" : "Existing Profile"}
                        </dd>

                        {!isNew && (
                            <>
                                <dt className="col-sm-4">Profile</dt>
                                <dd className="col-sm-8">{selectedProfile || "N/A"}</dd>
                            </>
                        )}
                        
                        {isNew && (
                           <>
                                <dt className="col-sm-4">Name</dt>
                                <dd className="col-sm-8">{runtimeVariables?.new_profile_name || "-"}</dd>
                           </> 
                        )}
                    </dl>
                </div>
            </div>

            {isNew && (
                <div className="form-check mb-3">
                    <input
                        className="form-check-input"
                        type="checkbox"
                        id="saveAsProfile"
                        checked={saveAsProfile}
                        onChange={(e) => setSaveAsProfile(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="saveAsProfile">
                        Save this configuration as a new Device Profile for future use
                    </label>
                </div>
            )}
            
            <FinishPage />
        </div>
    );
}

export default function AddDeviceConfigWizard(props) {
    const handleFinish = async (emittedManifest) => {
        // Prepare the final payload
        const isNew = emittedManifest.config_source === "new";
        
        let finalConfig = {};
        
        if (isNew) {
            finalConfig = {
                model: emittedManifest.model,
                signals: emittedManifest.signals,
            };
        } else {
            // Use the config selected from the list (synced field)
            finalConfig = emittedManifest.selected_config_data || {};
        }
        
        const payload = {
            ...emittedManifest,
            configuration: finalConfig,
        };
        
        if (props.onFinish) {
            await props.onFinish(payload);
        }
    };

    return (
        <WizardView
            onClose={props.onClose}
            onFinish={handleFinish}
            title="Configure Device"
            width="1100px"
            height="600px"
            showFinishPage={false} 
        >
            <WizardView.Page
                title="Source"
                icon="cog"
                onNextRequested={(e) => {
                    const { config_source, selected_profile_id, new_profile_name } = e.detail.runtimeVariables;
                    if (config_source === "existing" && !selected_profile_id) {
                        e.detail.prevented = true;
                    }
                    if (config_source === "new" && !new_profile_name) {
                        e.detail.prevented = true;
                    }
                }}
            >
                <ConfigSourcePage />
            </WizardView.Page>

            <WizardView.Page
                title="Device Type"
                icon="microchip"
                condition={(vars) => vars.config_source === "new"}
                onNextRequested={(e) => {
                    const selectedDevice = e.detail.runtimeVariables?.selected_device;
                    if (!selectedDevice) {
                        e.detail.prevented = true;
                    }
                }}
            >
                <DeviceTypePage />
            </WizardView.Page>

            <WizardView.Page 
                title="Signals" 
                icon="arrow-down"
                condition={(vars) => vars.config_source === "new"}
            >
                <SignalsPage />
            </WizardView.Page>

            <WizardView.Page title="Review" icon="check-circle">
                <ReviewPage />
            </WizardView.Page>
        </WizardView>
    );
}
